import { useEffect, useState } from 'react';
import { useActiveContext } from '../hooks/useActiveContext';
import type { WidgetProps } from './WidgetRegistry';
import type { ScopedRef } from '../services/api';

/**
 * ActiveContext widget (KB Doc 142 §5.6) — the user must SEE what the agent is
 * answering about. Amber/red flag chips, editable scope, re-resolve.
 */
export function ActiveContextWidget({ journalRootId }: WidgetProps) {
  const { context, loading, error, load, setScope, reResolve } = useActiveContext();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ customer: '', site: '', diet: '', cls: '', subcls: '' });

  useEffect(() => { if (journalRootId) load(journalRootId); }, [journalRootId]);

  useEffect(() => {
    if (context) {
      setForm({
        customer: context.customer.name, site: context.site.name, diet: context.diet.name,
        cls: context.class || '', subcls: context.subclass || '',
      });
    }
  }, [context?.revision]);

  const flagChip = (label: string, on: boolean, color: string) =>
    on ? <span key={label} className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${color}`}>{label}</span> : null;

  const refDisplay = (label: string, r: ScopedRef) => {
    if (!r.name && !r.id) return null;
    return (
      <div key={label} className="flex items-baseline gap-2">
        <span className="text-[10px] text-tower-muted w-16 shrink-0">{label}</span>
        <span className="text-xs text-tower-text">{r.name || '(unnamed)'}</span>
        {r.id
          ? <span className="text-[10px] text-success font-mono">id {r.id}</span>
          : r.name ? <span className="text-[10px] text-warning">unresolved</span> : null}
      </div>
    );
  };

  const save = async () => {
    await setScope({
      customer: { name: form.customer }, site: { name: form.site }, diet: { name: form.diet },
      class: form.cls || null, subclass: form.subcls || null,
    }, 'manual edit in Active Context widget');
    setEditing(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1 border-b border-tower-border/50 shrink-0 flex items-center gap-2">
        <span className="text-xs text-tower-muted font-semibold">Active Context</span>
        <div className="flex-1" />
        <button onClick={() => journalRootId && reResolve()} disabled={loading}
          title="Re-resolve ids + freshness"
          className="text-[11px] text-tower-muted hover:text-tower-accent disabled:opacity-50">⟳ Re-resolve</button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {error && <div className="text-[11px] text-error">{error}</div>}
        {!context && !error && <div className="text-[11px] text-tower-muted">{loading ? 'Loading…' : 'No journal selected.'}</div>}

        {context && !editing && (
          <>
            {refDisplay('Customer', context.customer)}
            {refDisplay('Site', context.site)}
            {refDisplay('Diet', context.diet)}
            {(context.class || context.subclass) && (
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] text-tower-muted w-16 shrink-0">Class</span>
                <span className="text-xs text-tower-text">{context.class || '—'} / {context.subclass || '—'}</span>
              </div>
            )}
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] text-tower-muted w-16 shrink-0">Source</span>
              <span className="text-[11px] text-tower-muted">
                id={context.site.resolvedFrom || context.customer.resolvedFrom || '—'} · data={context.primarySource}
                {context.snapshotAt ? ` · snapshot ${new Date(context.snapshotAt).toLocaleString()}` : ''}
              </span>
            </div>

            <div className="flex flex-wrap gap-1 pt-1">
              {flagChip('STALE', context.flags.stale, 'bg-warning/20 text-warning')}
              {flagChip('AMBIGUOUS', context.flags.ambiguous, 'bg-warning/20 text-warning')}
              {flagChip('CONFLICT', context.flags.conflict, 'bg-error/25 text-error')}
              {flagChip('UNRESOLVED', context.flags.unresolved, 'bg-warning/20 text-warning')}
            </div>
            {context.notes.length > 0 && (
              <div className="text-[10px] text-tower-muted leading-snug pt-0.5">{context.notes[0]}</div>
            )}

            <button onClick={() => setEditing(true)}
              className="mt-1 text-[11px] px-2 py-0.5 bg-tower-border/40 hover:bg-tower-border text-tower-text rounded">
              Edit scope ▾
            </button>
          </>
        )}

        {context && editing && (
          <div className="space-y-1.5">
            {(['customer', 'site', 'diet'] as const).map(k => (
              <div key={k}>
                <label className="text-[10px] text-tower-muted capitalize">{k}</label>
                <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
                  className="w-full bg-tower-bg text-tower-text text-xs border border-tower-border rounded px-1.5 py-1" />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-1.5">
              <input value={form.cls} onChange={e => setForm({ ...form, cls: e.target.value })} placeholder="Class"
                className="bg-tower-bg text-tower-text text-xs border border-tower-border rounded px-1.5 py-1" />
              <input value={form.subcls} onChange={e => setForm({ ...form, subcls: e.target.value })} placeholder="Subclass"
                className="bg-tower-bg text-tower-text text-xs border border-tower-border rounded px-1.5 py-1" />
            </div>
            <div className="flex gap-1.5 pt-1">
              <button onClick={save} disabled={loading}
                className="text-[11px] px-2 py-0.5 bg-primary hover:bg-primary text-textOnPrimary rounded disabled:opacity-50">Save</button>
              <button onClick={() => setEditing(false)} className="text-[11px] px-2 py-0.5 text-tower-muted hover:text-tower-text">Cancel</button>
            </div>
            <p className="text-[10px] text-tower-muted">A name change re-resolves its id from the source of record on the next tool run.</p>
          </div>
        )}
      </div>
    </div>
  );
}
